import React from "react"
import { useTranslation } from "react-i18next"
import { Navigate, Route, Routes } from "react-router-dom"

const Transportation = React.lazy(() => import("pages/Transportation"))
const UpsertTransportation = React.lazy(() => import("pages/Transportation/UpsertTransportation"))
const Event = React.lazy(() => import("pages/Event"))
const UpsertEvent = React.lazy(() => import("pages/Event/UpsertEvent"))
const EventCategories = React.lazy(() => import("pages/EventCategories"))
const Mall = React.lazy(() => import("pages/Mall"))
const UpsertMall = React.lazy(() => import("pages/Mall/UpsertMall"))
const MallCategories = React.lazy(() => import("pages/MallCategories"))
const TravelDestination = React.lazy(() => import("pages/TravelDestination"))
const UpsertTravelDestination = React.lazy(() =>
  import("pages/TravelDestination/UpsertTravelDestination")
)
const TravelDestinationCategories = React.lazy(() => import("pages/TravelDestinationCategories"))
const CulturalHistorical = React.lazy(() => import("pages/CulturalHistorical"))
const UpsertCulturalHistorical = React.lazy(() =>
  import("pages/CulturalHistorical/UpsertCulturalHistorical")
)
const CulturalHistoricalCategories = React.lazy(() => import("pages/CulturalHistoricalCategories"))
const Hotel = React.lazy(() => import("pages/Hotel"))
const UpsertHotel = React.lazy(() => import("pages/Hotel/UpsertHotel"))
const Tour = React.lazy(() => import("pages/Tour"))
const UpsertTour = React.lazy(() => import("pages/Tour/UpsertTour"))
const TourCategories = React.lazy(() => import("pages/TourCategories"))
const TourDurations = React.lazy(() => import("pages/TourDurations"))
const TourTypes = React.lazy(() => import("pages/TourTypes"))
const TravelCompany = React.lazy(() => import("pages/TravelCompany"))
const UpsertTravelCompany = React.lazy(() => import("pages/TravelCompany/UpsertTravelCompany"))
const TravelCompanyTags = React.lazy(() => import("pages/TravelCompanyTags"))
const StateAgency = React.lazy(() => import("pages/StateAgency"))
const UpsertStateAgency = React.lazy(() => import("pages/StateAgency/UpsertStateAgency"))
const StateAgencyCategories = React.lazy(() => import("pages/StateAgencyCategories"))
const Restaurant = React.lazy(() => import("pages/Restaurant"))
const UpsertRestaurant = React.lazy(() => import("pages/Restaurant/UpsertRestaurant"))
const RestaurantTags = React.lazy(() => import("pages/RestaurantTags"))
const Food = React.lazy(() => import("pages/Food"))
const UpsertFood = React.lazy(() => import("pages/Food/UpsertFood"))
const FoodCategories = React.lazy(() => import("pages/FoodCategories"))
const FoodTypes = React.lazy(() => import("pages/FoodTypes"))
const EntertainmentVenue = React.lazy(() => import("pages/EntertainmentVenue"))
const UpsertEntertainmentVenue = React.lazy(() =>
  import("pages/EntertainmentVenue/UpsertEntertainmentVenue")
)
const EntertainmentVenueCategories = React.lazy(() =>
  import("pages/EntertainmentVenueCategories")
)
const MedicalCenter = React.lazy(() => import("pages/MedicalCenter"))
const UpsertMedicalCenter = React.lazy(() => import("pages/MedicalCenter/UpsertMedicalCenter"))
const MedicalCenterCategories = React.lazy(() => import("pages/MedicalCenterCategories"))
const MedicalCenterDepartments = React.lazy(() => import("pages/MedicalCenterDepartments"))
const PoliceHeadquarter = React.lazy(() => import("pages/PoliceHeadquarter"))
const UpsertPoliceHeadquarter = React.lazy(() => import("pages/PoliceHeadquarter/UpsertPoliceHeadquarter"))
const PoliceHeadquarterCategories = React.lazy(() => import("pages/PoliceHeadquarterCategories"))
const Settings = React.lazy(() => import("pages/Settings"))
const RoleSetting = React.lazy(() => import("pages/Settings/RoleSetting"))

const MainRoutes = () => {
  const { t } = useTranslation()

  return (
    <React.Suspense fallback={<span>{t("loading")}</span>}>
      <Routes>
        <Route path="/" element={<Navigate to="/transportation" />} />
        <Route path="/transportation" element={<Transportation />} />
        <Route path="/transportation/create" element={<UpsertTransportation />} />
        <Route path="/transportation/:id/edit" element={<UpsertTransportation />} />

        <Route path="/events" element={<Event />} />
        <Route path="/events/create" element={<UpsertEvent />} />
        <Route path="/events/:id/edit" element={<UpsertEvent />} />
        <Route path="/events/categories" element={<EventCategories />} />

        <Route path="/malls" element={<Mall />} />
        <Route path="/malls/create" element={<UpsertMall />} />
        <Route path="/malls/:id/edit" element={<UpsertMall />} />
        <Route path="/malls/categories" element={<MallCategories />} />

        <Route path="/travel-destinations" element={<TravelDestination />} />
        <Route path="/travel-destinations/create" element={<UpsertTravelDestination />} />
        <Route path="/travel-destinations/:id/edit" element={<UpsertTravelDestination />} />
        <Route path="/travel-destinations/categories" element={<TravelDestinationCategories />} />

        <Route path="/cultural-historical" element={<CulturalHistorical />} />
        <Route path="/cultural-historical/create" element={<UpsertCulturalHistorical />} />
        <Route path="/cultural-historical/:id/edit" element={<UpsertCulturalHistorical />} />
        <Route path="/cultural-historical/categories" element={<CulturalHistoricalCategories />} />

        <Route path="/hotels" element={<Hotel />} />
        <Route path="/hotels/create" element={<UpsertHotel />} />
        <Route path="/hotels/:id/edit" element={<UpsertHotel />} />

        <Route path="/tours" element={<Tour />} />
        <Route path="/tours/create" element={<UpsertTour />} />
        <Route path="/tours/:id/edit" element={<UpsertTour />} />
        <Route path="/tours/categories" element={<TourCategories />} />
        <Route path="/tours/durations" element={<TourDurations />} />
        <Route path="/tours/types" element={<TourTypes />} />

        <Route path="/travel-companies" element={<TravelCompany />} />
        <Route path="/travel-companies/create" element={<UpsertTravelCompany />} />
        <Route path="/travel-companies/:id/edit" element={<UpsertTravelCompany />} />
        <Route path="/travel-companies/tags" element={<TravelCompanyTags />} />

        <Route path="/state-agencies" element={<StateAgency />} />
        <Route path="/state-agencies/create" element={<UpsertStateAgency />} />
        <Route path="/state-agencies/:id/edit" element={<UpsertStateAgency />} />
        <Route path="/state-agencies/categories" element={<StateAgencyCategories />} />

        <Route path="/restaurants" element={<Restaurant />} />
        <Route path="/restaurants/create" element={<UpsertRestaurant />} />
        <Route path="/restaurants/:id/edit" element={<UpsertRestaurant />} />
        <Route path="/restaurants/tags" element={<RestaurantTags />} />

        <Route path="/foods" element={<Food />} />
        <Route path="/foods/create" element={<UpsertFood />} />
        <Route path="/foods/:id/edit" element={<UpsertFood />} />
        <Route path="/foods/categories" element={<FoodCategories />} />
        <Route path="/foods/types" element={<FoodTypes />} />

        <Route path="/entertainment-venue" element={<EntertainmentVenue />} />
        <Route path="/entertainment-venue/create" element={<UpsertEntertainmentVenue />} />
        <Route path="/entertainment-venue/:id/edit" element={<UpsertEntertainmentVenue />} />
        <Route path="/entertainment-venue/categories" element={<EntertainmentVenueCategories />} />

        <Route path="/medical-centers" element={<MedicalCenter />} />
        <Route path="/medical-centers/create" element={<UpsertMedicalCenter />} />
        <Route path="/medical-centers/:id/edit" element={<UpsertMedicalCenter />} />
        <Route path="/medical-centers/categories" element={<MedicalCenterCategories />} />
        <Route path="/medical-centers/departments" element={<MedicalCenterDepartments />} />

        <Route path="/police-headquarters" element={<PoliceHeadquarter />} />
        <Route path="/police-headquarters/create" element={<UpsertPoliceHeadquarter />} />
        <Route path="/police-headquarters/:id/edit" element={<UpsertPoliceHeadquarter />} />
        <Route path="/police-headquarters/categories" element={<PoliceHeadquarterCategories />} />

        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/role" element={<RoleSetting />} />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </React.Suspense>
  )
}

export default MainRoutes
